// src/modules/menu/services/menuService.js

import authService from '../../auth/services/authService';
import { buildApiUrl } from '../../../config/api';

// Helper para manejar respuestas y redirigir en caso de 401
const handleResponse = async (response) => {
    if (response.status === 401) {
        authService.logout();
        window.location.href = '/login';
        throw new Error('Token expirado');
    }
    return response;
};

export const menuService = {
    /**
     * Generar menú para usuarios logueados
     */
    async generateMenu(preferences) {
        const response = await fetch(buildApiUrl('/api/user/generate-menu'), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${authService.getToken()}`,
            },
            credentials: 'include',
            body: JSON.stringify(preferences),
        });

        await handleResponse(response);

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Error al generar el menú');
        }

        return await response.json();
    },

    /**
     * Obtener estado de menús para usuarios logueados
     */
    async getMenuStatus() {
        const response = await fetch(buildApiUrl('/api/user/menu-status'), {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${authService.getToken()}`,
            },
            credentials: 'include',
        });

        await handleResponse(response);

        if (!response.ok) {
            throw new Error('Error al obtener el estado de los menús');
        }

        return await response.json();
    },

    /**
     * Normalizar respuesta del plan para manejar estructura legacy y nueva
     */
    normalizePlanResponse(response) {
        // Si tiene current_week, usar estructura nueva
        if (response.current_week !== undefined) {
            return {
                hasNewStructure: true,
                currentWeek: {
                    menus: response.current_week.menus || [],
                    metadata: response.current_week.menu_metadata || null
                },
                nextWeek: {
                    menus: response.next_week.menus || [],
                    metadata: response.next_week.menu_metadata || null
                },
                previousWeeks: response.previous_weeks || {},
                // Mantener legacy para compatibilidad
                legacy: {
                    menus: response.menus || [],
                    metadata: response.menu_metadata || null
                }
            };
        }
        
        // Estructura legacy
        return {
            hasNewStructure: false,
            currentWeek: {
                menus: [],
                metadata: null
            },
            nextWeek: {
                menus: response.menus || [],
                metadata: response.menu_metadata || null
            },
            previousWeeks: {},
            legacy: {
                menus: response.menus || [],
                metadata: response.menu_metadata || null
            }
        };
    },

    /**
     * Obtener todos los menús guardados para usuarios logueados
     */
    async getAllMenus() {
        const response = await fetch(buildApiUrl('/api/user/plan'), {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${authService.getToken()}`,
            },
            credentials: 'include',
        });

        await handleResponse(response);

        if (!response.ok) {
            throw new Error('Error al obtener los menús');
        }

        const data = await response.json();
        
        // Normalizar respuesta para compatibilidad
        return this.normalizePlanResponse(data);
    },

    /**
     * Reordenar menús por fechas
     */
    async reorderMenus(dateMapping) {
        const response = await fetch(buildApiUrl('/api/user/reorder-menus'), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${authService.getToken()}`,
            },
            credentials: 'include',
            body: JSON.stringify({ dateMapping }),
        });

        await handleResponse(response);

        if (!response.ok) {
            throw new Error('Error al reordenar los menús');
        }

        return await response.json();
    },

    /**
     * Reordenar comidas dentro de un día específico
     */
    async reorderMeals(date, typeMapping) {
        const response = await fetch(buildApiUrl('/api/user/reorder-meals'), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${authService.getToken()}`,
            },
            credentials: 'include',
            body: JSON.stringify({ date, typeMapping }),
        });

        await handleResponse(response);

        if (!response.ok) {
            throw new Error('Error al reordenar las comidas');
        }

        return await response.json();
    },

    /**
     * Confirmar semana y generar lista de compra
     */
    async confirmWeekAndGenerateShoppingList() {
        const response = await fetch(buildApiUrl('/api/user/confirm-week'), {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${authService.getToken()}`,
            },
            credentials: 'include',
        });

        await handleResponse(response);

        if (!response.ok) {
            throw new Error('Error al confirmar la semana');
        }

        return await response.json();
    },

    /**
     * Obtener menú por semana
     */
    async getMenuByWeek(weekStartDate) {
        const response = await fetch(buildApiUrl(`/api/user/menus/week?week_start_date=${weekStartDate}`), {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${authService.getToken()}`,
            },
            credentials: 'include',
        });

        await handleResponse(response);

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Error al obtener el menú');
        }

        return await response.json();
    },

    /**
     * Obtener todos los menús del usuario
     */
    async getUserMenus(status = null) {
        const params = status ? `?status=${status}` : '';
        const response = await fetch(buildApiUrl(`/api/user/menus/user${params}`), {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${authService.getToken()}`,
            },
            credentials: 'include',
        });

        await handleResponse(response);

        if (!response.ok) {
            throw new Error('Error al obtener los menús del usuario');
        }

        return await response.json();
    },

    /**
     * Obtener preferencias del usuario
     */
    async getPreferences() {
        const response = await fetch(buildApiUrl('/api/user/preferences'), {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${authService.getToken()}`,
            },
            credentials: 'include',
        });

        await handleResponse(response);

        if (!response.ok) {
            throw new Error('Error al obtener las preferencias');
        }

        return await response.json();
    },

    /**
     * Obtener menú por fecha específica
     */
    async getMenuByDate(date) {
        const response = await fetch(buildApiUrl(`/api/user/plan/${date}`), {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${authService.getToken()}`,
            },
            credentials: 'include',
        });

        await handleResponse(response);

        if (!response.ok) {
            if (response.status === 404) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Menú no encontrado');
            }
            throw new Error('Error al obtener el menú');
        }

        return await response.json();
    },

    /**
     * Actualizar preferencias del usuario
     */
    async updatePreferences(preferences) {
        try {
            const response = await fetch(buildApiUrl('/api/user/preferences'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authService.getToken()}`,
                },
                credentials: 'include',
                body: JSON.stringify(preferences),
            });

            await handleResponse(response);

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                const errorMessage = errorData.error || errorData.message || 'Error al actualizar las preferencias';
                throw new Error(errorMessage);
            }

            return await response.json();
        } catch (error) {
            throw error;
        }
    }
};
